import { useQuery } from '@redwoodjs/web'

import { GET_DOCUMENT_IDS } from 'src/graphql/documents.graphql'

// Where a document sits in the full (unpaged) list of an applicant's
// documents, so the preview can step to the previous/next one even when the
// neighbour is on another page of the table. The list is fetched when the
// preview opens and deliberately not refetched on edits: a document that stops
// matching the filter after an edit keeps its place until the user steps away.
export const useDocumentListNavigation = ({
  applicantId,
  documentId,
  filters,
  skip = false,
}: {
  applicantId: string
  documentId: string | null
  filters?: Record<string, unknown>
  skip?: boolean
}) => {
  const hasFilters = !!filters && Object.keys(filters).length > 0
  const { data, previousData, loading } = useQuery(GET_DOCUMENT_IDS, {
    variables: { applicantId, ...(hasFilters && { filters }) },
    skip: skip || !applicantId,
    fetchPolicy: 'cache-and-network',
  })

  // previousData keeps the counter on screen while a changed filter reloads
  const available = data ?? previousData
  const items = available?.documents?.items ?? []
  const ids: string[] = items.map((item) => item.documentId)
  const index = documentId ? ids.indexOf(documentId) : -1

  return {
    ids,
    // True only until the first list arrives; a refetch with a list already on
    // screen is not "loading" (the navigation keeps working meanwhile)
    loading: !skip && !!applicantId && loading && !available,
    index,
    total: ids.length,
    previousId: index > 0 ? ids[index - 1] : undefined,
    nextId: index >= 0 && index < ids.length - 1 ? ids[index + 1] : undefined,
  }
}
